import { useNavigate, useParams } from "@tanstack/solid-router";
import { createMutation, createQuery } from "@tanstack/solid-query";
import { Show } from "solid-js";
import { SkillChips } from "~/components/SkillChips";
import { fetchProfile, fetchProject, reportProject } from "~/lib/api";
import { profileQueryKey } from "~/lib/routes";

export function ProjectDetailPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const projectId = () => params().projectId ?? "";

  const projectQuery = createQuery(() => ({
    queryKey: ["project", projectId()],
    queryFn: () => fetchProject(projectId()),
    enabled: !!projectId(),
  }));
  const myProfileQuery = createQuery(() => ({
    queryKey: profileQueryKey(),
    queryFn: () => fetchProfile(),
  }));

  const reportMutation = createMutation(() => ({
    mutationFn: (reason: string) => reportProject(projectId(), reason),
    onSuccess: () => alert("Report submitted. The team will review it."),
    onError: (err: unknown) => alert((err as Error).message),
  }));

  const project = () => projectQuery.data?.data;
  const isOwn = () =>
    !!project()?.user_id && project()?.user_id === myProfileQuery.data?.data?.user_id;

  function onReport() {
    const reason = window.prompt("Why are you reporting this project? (required)");
    if (!reason?.trim()) return;
    reportMutation.mutate(reason.trim());
  }

  function goOwner() {
    const userId = project()?.user_id;
    if (!userId) return;
    navigate({ to: "/profile/$profileId", params: { profileId: userId } });
  }

  return (
    <section class="card">
      <Show when={projectQuery.isPending}>
        <p class="muted">Loading project…</p>
      </Show>
      <Show when={projectQuery.isError}>
        <div class="error-box">{(projectQuery.error as Error).message}</div>
        <button type="button" class="btn btn-ghost" onClick={() => navigate({ to: "/directory" })}>
          Back to directory
        </button>
      </Show>
      <Show when={project()} keyed>
        {(p) => (
          <>
            <div class={`project-detail-cover${p.cover_image_url ? "" : " project-card-cover--gradient"}`}>
              <Show when={p.is_pinned}>
                <span class="status-chip">Pinned</span>
              </Show>
            </div>
            <h1>{p.title}</h1>
            <Show when={p.summary}>
              <p class="profile-preview-headline">{p.summary}</p>
            </Show>
            <SkillChips skills={p.skills} />
            <Show when={p.description}>
              <p class="profile-detail-bio">{p.description}</p>
            </Show>
            <div class="project-links">
              <Show when={p.repo_url}>
                <a href={p.repo_url} target="_blank" rel="noreferrer">
                  Repo
                </a>
              </Show>
              <Show when={p.demo_url}>
                <a href={p.demo_url} target="_blank" rel="noreferrer">
                  Demo
                </a>
              </Show>
              <Show when={p.article_url}>
                <a href={p.article_url} target="_blank" rel="noreferrer">
                  Article
                </a>
              </Show>
            </div>
            <div class="wizard-actions">
              <button type="button" class="btn btn-ghost" onClick={() => navigate({ to: "/directory" })}>
                Back
              </button>
              <Show when={p.user_id}>
                <button type="button" class="btn btn-outline" onClick={goOwner}>
                  View profile
                </button>
              </Show>
              <Show when={isOwn()}>
                <button
                  type="button"
                  class="btn btn-primary"
                  onClick={() =>
                    navigate({ to: "/me/projects/$projectId", params: { projectId: p.id } })
                  }
                >
                  Edit
                </button>
              </Show>
              <Show when={!isOwn()}>
                <button type="button" class="btn btn-ghost" onClick={onReport}>
                  Report
                </button>
              </Show>
            </div>
          </>
        )}
      </Show>
    </section>
  );
}
